import algoliasearchHelper, {
  type AlgoliaSearchHelper,
} from 'algoliasearch-helper';
import clsx from 'clsx';
import { TypedEventBus } from 'dotcom-core/src/components/EventBus';
import { pushDataEvent } from 'dotcom-core/src/helpers/tracking';
import {
  COUNTRY_COOKIE,
  COUNTRY_HEADER,
  DEFAULT_COUNTRY,
  STORE_CODE_DEFAULT,
} from 'dotcom-core/src/constants';
import { log } from 'dotcom-core/src/helpers/logServer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type ClientLoaderFunctionArgs,
  Link,
  type LoaderFunctionArgs,
  type MetaFunction,
  data,
  generatePath,
  redirect,
  useLoaderData,
  useLocation,
  useSearchParams,
} from 'react-router';
import type { Language } from '~/graphql';
import {
  FACET_SEPARATOR,
  SORT_VALUES,
  type SortValue,
  algoliaSearchClient,
  getAlgoliaIndex,
  getAlgoliaWebshopUserData,
} from '~/helpers/algolia';
import { ROUTE_SEGMENTS } from '~/helpers/app-routes';
import { ROUTE_PATHS } from '~/helpers/app-routes';
import { getIsAuthenticated, verifyUserSession } from '~/helpers/auth.server';
import { makeClient } from '~/helpers/client';
import { getCookie } from '~/helpers/cookie';
import { logSentryError } from '~/helpers/error';
import { useIntl } from '~/helpers/intl';
import type {
  AlgoliaProductHit,
  AlgoliaProductHitAttribute,
} from '~/helpers/product';
import type { Route } from '../+types';
import { AudioPlayerProvider } from './AudioPlayer';
import { FilterMenu } from './FilterMenu';
import { FilterStateProvider } from './FilterStateContext';
import { ProductCard } from './ProductCard';
import { SearchBar } from './SearchBar';
import SOSDesktopBanner from './assets/sos_banner_desktop.jpg';
import SOSMobileBanner from './assets/sos_banner_mobile.jpg';

function CatalogPage() {
  const {
    hits,
    isFirstPage,
    isLastPage,
    nbPages,
    page,
    storeCode,
    totalHits,
    query,
  } = useLoaderData<CatalogLoader>();

  const { t, tReact } = useIntl();

  const audioPlayerRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const location = useLocation();
  const [currentPathname, setCurrentPathname] = useState(location.pathname);

  // When the pathname of the page change, we want to remove pagination search field.
  useEffect(() => {
    if (location.pathname !== currentPathname) {
      searchParams.delete('page');
      setSearchParams(searchParams);
      setCurrentPathname(location.pathname);
    }
  }, [searchParams, setSearchParams, location.pathname, currentPathname]);

  useRouteChangeListener();

  const [pageListShown, setPageListShown] = useState([page]);
  const [currentHits, setCurrentHits] = useState<AlgoliaProductHit[]>(hits);

  const getPageQuery = useCallback(
    (page: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', String(page));
      return newSearchParams.toString();
    },
    [searchParams],
  );

  const { previousPage, nextPage } = useMemo(() => {
    let nextPage = Number.MIN_VALUE;
    let previousPage = Number.MAX_VALUE;

    pageListShown.forEach((p) => {
      if (p > nextPage) nextPage = p;
      if (p < previousPage) previousPage = p;
    });

    previousPage -= 1;
    nextPage += 1;

    return { previousPage, nextPage };
  }, [pageListShown]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: This hooks *must* only be re-render on page updates, trust me.
  useEffect(() => {
    const list =
      previousPage < page
        ? [...currentHits, ...hits]
        : [...hits, ...currentHits];

    setCurrentHits(
      list.filter(({ objectID }, index, array) => {
        return array.findIndex((val) => val.objectID === objectID) === index;
      }),
    );

    setPageListShown([...pageListShown, page]);
  }, [page]);

  // When the user is doing a sort, or a query search; we want to reset the pagination,
  // that's what this useEffect is doing.
  useEffect(() => {
    if (!searchParams.get('page')) {
      setCurrentHits(hits);
      setPageListShown([page]);
    }
  }, [searchParams, hits, page]);

  const shouldDisplayPrevious = !isFirstPage && previousPage >= 0;
  const shouldDisplayNextPage = !isLastPage && nextPage < nbPages;

  const shouldDisplaySOSBanner = location.pathname.includes(
    'summer-of-sound-2025',
  );

  return (
    <>
      <div className="container py-3 lg:py-5 relative">
        {shouldDisplaySOSBanner && (
          <div
            className={clsx(
              'relative inline-block flex flex-col justify-center items-center h-[220px] w-full overflow-hidden mb-3',
            )}
          >
            <picture>
              <source
                media="(max-width: 800px)"
                srcSet={SOSMobileBanner}
                type="image/jpg"
              />
              <img
                src={SOSDesktopBanner}
                alt={t('sos.title', {
                  fallback: 'This is your Summer of Sound',
                })}
                className="absolute top-0 left-0 h-full w-full object-cover"
              />
            </picture>
            <div className="absolute p-3 text-grey-90 text-center">
              <h3 className="font-medium webex-h3 mb-1">
                {t('sos.title', { fallback: 'This is your Summer of Sound' })}
              </h3>
              <p className="text-md">
                {t('sos.description', {
                  fallback:
                    'Get all the tools you need with hundreds of savings across software and hardware.',
                })}
              </p>
            </div>
          </div>
        )}

        <FilterStateProvider>
          <FilterMenu />
          <SearchBar />
        </FilterStateProvider>

        <AudioPlayerProvider audioPlayerRef={audioPlayerRef}>
          {shouldDisplayPrevious && (
            <div className="pb-6 text-center">
              <span
                data-testid="previous-pagination-count"
                className="text-xs text-grey-60"
              >
                {t('catalog.numberListing', {
                  fallback: '{value} products out of {totalHits}',
                  values: {
                    value: currentHits?.length,
                    totalHits,
                  },
                })}
              </span>
              <div className="flex justify-center pt-1">
                <Link
                  preventScrollReset={true}
                  className="webex-btn webex-btn--primary webex-btn--regular"
                  data-testid="load-previous-button"
                  to={{ search: getPageQuery(previousPage) }}
                >
                  {t('catalog.pagination.previous', {
                    fallback: 'Show previous results',
                  })}
                </Link>
              </div>
            </div>
          )}

          {totalHits === 0 && query ? (
            <div className="text-center py-10" data-testid="no-results-message">
              {tReact('catalog.emptyState', {
                fallback: 'There are no results for {querySearch}',
                values: {
                  querySearch: () => (
                    <span
                      data-testid="no-results-item-title"
                      className="font-medium"
                    >
                      {query}
                    </span>
                  ),
                },
              })}
            </div>
          ) : (
            <div
              className={clsx(
                'grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-y-3 gap-x-3 justify-items-center',
                'auto-rows-max',
              )}
            >
              {currentHits.map((hit, index) => (
                <ProductCard
                  index={index}
                  key={hit.objectID}
                  hit={hit}
                  storeCode={storeCode}
                />
              ))}
            </div>
          )}

          {shouldDisplayNextPage && (
            <div className="pt-6 text-center">
              <span
                data-testid="more-pagination-count"
                className="text-xs text-grey-60"
              >{`${currentHits.length} products out of ${totalHits}`}</span>

              <div className="flex justify-center pt-1">
                <Link
                  preventScrollReset={true}
                  className="webex-btn webex-btn--primary webex-btn--regular"
                  data-testid="load-more-button"
                  to={{ search: getPageQuery(nextPage) }}
                >
                  {t('components.ProductList.loadMore', {
                    fallback: 'Show more results',
                  })}
                </Link>
              </div>
            </div>
          )}
        </AudioPlayerProvider>
      </div>

      <div
        className="sticky w-full bottom-0 z-50"
        ref={audioPlayerRef}
        id="audio-player"
      />
    </>
  );
}

const useRouteChangeListener = () => {
  const location = useLocation();

  useEffect(() => {
    TypedEventBus.emit('ni-v2/route-changed', { route: location.pathname });
  }, [location]);
};

async function clientLoader(loaderArgs: ClientLoaderFunctionArgs) {
  /*
   *  we only want to search again on the client side
   * further data can come from the server
   */
  const serverLoaderData = await loaderArgs.serverLoader<CatalogLoader>();
  const catalogResults = await searchProducts({
    loaderArgs,
    licenseIDs: serverLoaderData?.licenseIDs,
    nativeId: serverLoaderData.nativeId,
    storeCode: serverLoaderData.storeCode,
    country: serverLoaderData.country,
  });

  return {
    ...serverLoaderData,
    ...catalogResults,
  };
}

function getCatalogSlugs(url: URL) {
  return url.pathname
    .split(`${ROUTE_SEGMENTS.catalog}/`)[1]
    .replace(/\/?$/, '/');
}

export function checkIfOldPathnameAndRedirect({
  url,
  redirects,
}: {
  url: URL;
  redirects?: Record<string, string>;
}): string {
  let redirectPathname = '';
  const currentPathname = getCatalogSlugs(url);

  Object.entries(redirects ?? {}).forEach(([oldPathname, newPathname]) => {
    oldPathname = oldPathname.toLowerCase().replace(/\/?$/, '/');

    if (currentPathname.toLowerCase() === oldPathname) {
      redirectPathname = newPathname;
    }
  });

  return redirectPathname;
}

// Because we want to have a "slim" loader function.
export function isValidCatalogPathname({
  url,
  slugHierarchyFull,
}: {
  url: URL;
  slugHierarchyFull?: Record<string, string[]>;
}): boolean {
  // That's all available urls for the catalog.
  const availablePathnames = Object.values(slugHierarchyFull ?? {})
    .flatMap((s) => [...s])
    .map((s) => s.split(FACET_SEPARATOR).join('/').replace(/\/?$/, '/'));

  const currentPathname = getCatalogSlugs(url);

  return availablePathnames.includes(currentPathname);
}

async function loader(loaderArgs: LoaderFunctionArgs) {
  const slugs = loaderArgs.params['*'];
  const DEFAULT_ROUTE = 'software';

  if (slugs === '') return redirect(`${DEFAULT_ROUTE}/`);

  let licenseIDs: string[] = [];
  const language = loaderArgs.params.language as Language;
  const cookieHeader = loaderArgs.request.headers.get('Cookie');

  const country =
    (getCookie(cookieHeader, COUNTRY_COOKIE) ||
      loaderArgs.request.headers.get(COUNTRY_HEADER)) ??
    DEFAULT_COUNTRY;

  const { countryStoreMapping, slugMapping, slugHierarchyFull, redirects } =
    await getAlgoliaWebshopUserData(language);
  const storeCode = countryStoreMapping?.[country] || STORE_CODE_DEFAULT;

  const url = new URL(loaderArgs.request.url);

  const newPathname = checkIfOldPathnameAndRedirect({ url, redirects });
  if (newPathname) {
    return redirect(
      `${generatePath(ROUTE_PATHS.catalog, { language })}/${newPathname}`,
    );
  }

  if (!isValidCatalogPathname({ url, slugHierarchyFull })) {
    throw new Response(null, {
      status: 404,
      statusText: 'Not Found',
    });
  }

  const isAuthenticated = getIsAuthenticated(loaderArgs.request);
  let nativeId = undefined;

  const headers = new Headers();

  headers.append(
    'Cache-Control',
    isAuthenticated
      ? 'private'
      : /*
         * for 15min, cache is assumed fresh.
         * between 15–30min, stale cache is used while a revalidation happens.
         * after 30min, cache is invalid and we hit the network.
         */
        'public',
  );

  if (isAuthenticated) {
    const client = makeClient(loaderArgs);
    try {
      const userOwnProductIds = await client.UserOwnProductIds();
      licenseIDs = userOwnProductIds.me?.licenseIDs ?? [];
      const { session } = await verifyUserSession(loaderArgs.request, headers);
      nativeId = session.get('user')?.nativeId;
    } catch (error) {
      if (error instanceof Error) {
        log.warn({ errorName: error.name, message: error.message });

        logSentryError({
          name: error.name || '',
          message: error.message,
          source: 'server',
        });
      }
    }
  }

  const searchResults = await searchProducts({
    loaderArgs,
    licenseIDs,
    nativeId,
    storeCode,
    country,
  });

  return data(
    {
      ...searchResults,
      licenseIDs,
      nativeId,
      isAuthenticated,
      storeCode,
      country,
      slugMapping,
      slugHierarchyFull,
    },
    {
      headers,
    },
  );
}

const CATALOG_MENU_HIERARCHY_NAME = 'catalogMenu';
const searchClients: Record<string, AlgoliaSearchHelper> = {};

function getSearchClient(indexName: string) {
  searchClients[indexName] ??= algoliasearchHelper(
    // @ts-expect-error god knows
    algoliaSearchClient,
    indexName,
    {
      hierarchicalFacets: [
        {
          name: CATALOG_MENU_HIERARCHY_NAME,
          attributes: [
            'slug_hierarchy.lvl0',
            'slug_hierarchy.lvl1',
            'slug_hierarchy.lvl2',
          ] satisfies AlgoliaProductHitAttribute[],
        },
      ],
    },
  );

  return searchClients[indexName];
}

export function makeSafeSearchQueryParams(
  searchParams: URLSearchParams,
): QueryParams {
  const sort = searchParams.get('sort') ?? '';
  const hideProductsYouOwn = searchParams.get('hideProductsYouOwn') === 'true';

  let page = Number(searchParams.get('page'));
  if (!Number.isInteger(page) || page < 0 || page > Number.MAX_SAFE_INTEGER) {
    page = 0;
  }

  const queryParams: QueryParams = {
    hideProductsYouOwn,
    page,
    query: searchParams.get('query') ?? undefined,
    sort: !SORT_VALUES.includes(sort as SortValue)
      ? 'relevance'
      : (sort as SortValue),
  };

  return queryParams;
}

async function searchProducts({
  loaderArgs,
  licenseIDs,
  nativeId,
  storeCode,
  country,
}: {
  loaderArgs: LoaderFunctionArgs;
  licenseIDs: string[];
  nativeId?: string;
  storeCode: string;
  country: string;
}) {
  const { params, request } = loaderArgs;
  const language = params.language as Language;
  const url = new URL(request.url);
  const queryParams = makeSafeSearchQueryParams(url.searchParams);

  const indexName = getAlgoliaIndex({
    language,
    ...(queryParams.sort && { sort: queryParams.sort }),
  });
  const searchClient = getSearchClient(indexName);

  const filters = queryParams.hideProductsYouOwn
    ? licenseIDs?.map((id) => `NOT licensedproduct_ids=${id}`).join(' AND ')
    : '';

  const slugs = params['*'] || '';
  const menuRefinement = slugs
    ?.replace(/^\/|\/$/g, '') // no trailing slashes
    .replace(/\//g, FACET_SEPARATOR); // slashes -> separator

  const { content: response } = await searchClient.searchOnce({
    filters,
    clickAnalytics: true,
    enablePersonalization: true,
    page: queryParams.page,
    query: queryParams.query,
    maxValuesPerFacet: 100,
    hierarchicalFacetsRefinements: {
      [CATALOG_MENU_HIERARCHY_NAME]: [menuRefinement],
    },
    ruleContexts: [storeCode, country],
  });

  const {
    hits,
    queryID: queryId,
    page = 0,
    nbPages = 0,
    query,
    nbHits,
    hierarchicalFacets,
  } = response;

  const menuFacets = hierarchicalFacets.find(({ name }) => {
    return name === CATALOG_MENU_HIERARCHY_NAME;
  })?.data;

  /*
   * Virtual replicas, used in index sorting, may return fewer results
   * due to higher relevancy strictness.
   * So when a sorted hits amount is available, we prioritise that.
   */
  const totalHits = response.nbSortedHits || nbHits;

  pushDataEvent({
    event: 'catalog_search',
    queryId,
    query,
    indexName,
    nativeId,
    filters: menuRefinement,
  });

  return {
    // algoliasearch helper does not provide generic type for hits, so we cast
    // to avoid `any[]`
    hits: hits as AlgoliaProductHit[],
    queryId,
    isFirstPage: page === 0,
    isLastPage: page === nbPages || hits.length === totalHits,
    nbPages,
    totalHits,
    page,
    query,
    hideProductsYouOwn: queryParams.hideProductsYouOwn ?? false,
    menuRefinement,
    menuFacets,
  };
}

const meta: MetaFunction = ({ matches }) => {
  const { t } = useIntl();
  const rootMeta = matches.find((match) => match.id === 'root')?.meta ?? [];
  const catalogTitle = t('components.Catalog.title', {
    fallback: 'Catalog',
  });

  return [...rootMeta, { title: `Native Instruments - ${catalogTitle}` }];
};

type QueryParams = {
  hideProductsYouOwn?: boolean;
  sort?: SortValue;
  page?: number;
  query?: string;
};

type CatalogLoader = typeof loader;

const headers = ({ loaderHeaders }: Route.HeadersArgs) => {
  return { 'Cache-Control': loaderHeaders.get('Cache-Control') };
};

export { meta, loader, clientLoader, headers, type CatalogLoader };

export default CatalogPage;
